import React from 'react';
import styled from 'styled-components';

const StyledCard = styled.div`
  background-color: ${({ theme, $variant }) => {
    if ($variant === 'elevated') return theme.colors.background.elevated;
    if ($variant === 'outlined') return 'transparent';
    if ($variant === 'glass') return theme.colors.background.paper + 'CC';
    return theme.colors.background.paper;
  }};
  
  border-radius: ${({ $rounded }) => {
    if ($rounded === 'none') return '0';
    if ($rounded === 'small') return '8px';
    if ($rounded === 'large') return '24px';
    return '16px';
  }};
  
  padding: ${({ $padding }) => {
    switch ($padding) {
      case 'none':
        return '0';
      case 'small':
        return '1.25rem';
      case 'large':
        return '2.5rem';
      default:
        return '2rem';
    }
  }};
  
  border: ${({ $variant, theme }) => {
    if ($variant === 'outlined') return `1px solid ${theme.colors.border.default}`;
    return 'none';
  }};
  
  box-shadow: ${({ $variant, theme, $elevation }) => {
    if ($variant === 'glass') {
      return `0 8px 32px ${theme.colors.shadow.medium}`;
    }
    if ($variant === 'elevated' || $elevation) {
      return `0 2px 8px ${theme.colors.shadow.light}, 0 4px 16px ${theme.colors.shadow.medium}`;
    }
    if ($variant === 'outlined') {
      return 'none';
    }
    return `0 1px 3px ${theme.colors.shadow.light}`;
  }};
  
  backdrop-filter: ${({ $variant }) =>
    $variant === 'glass' ? 'blur(10px)' : 'none'
  };
  
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${({ $hoverable, theme }) => $hoverable && `
    cursor: pointer;
    
    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 24px ${theme.colors.shadow.medium}, 0 12px 32px ${theme.colors.shadow.dark};
      border-color: ${theme.colors.primary.main};
    }
  `}
  
  ${({ $accent, theme }) => $accent && `
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, ${theme.colors.primary.main} 0%, ${theme.colors.secondary.main} 100%);
    }
  `}
  
  ${({ $glow, theme }) => $glow && `
    box-shadow: ${theme.colors.shadow.glow};
  `}
  
  ${({ $fullHeight }) => $fullHeight && `
    height: 100%;
    display: flex;
    flex-direction: column;
  `}
  
  ${({ $gradient, theme }) => $gradient && `
    background: linear-gradient(135deg, 
      ${theme.colors.background.paper} 0%, 
      ${theme.colors.background.elevated} 100%);
  `}
`;

const Card = React.forwardRef(
  ({
    children,
    variant = 'default',
    padding = 'default',
    rounded = 'default',
    hoverable = false,
    accent = false,
    glow = false,
    elevation = false,
    fullHeight = false,
    gradient = false,
    ...props
  }, ref) => {
    return (
      <StyledCard
        ref={ref}
        $variant={variant}
        $padding={padding}
        $rounded={rounded}
        $hoverable={hoverable}
        $accent={accent}
        $glow={glow}
        $elevation={elevation}
        $fullHeight={fullHeight}
        $gradient={gradient}
        {...props}
      >
        {children}
      </StyledCard>
    );
  }
);

Card.displayName = 'Card';

export default Card;